import SingleLineChart from "../../components/charts/SingleLineChart";
import LineAxisChart from "../../components/charts/LineAxisChart";
import StackedBarChart from "../../components/charts/StackedBarChart";
import SecondBarChart from "../../components/charts/SecondBarChart";
import HeatmapChart from "../../components/charts/HeatmapChart";
import LoanTenure from "../../components/charts/LoanTenure";
import FinancialBarChart from "../../components/charts/FinancialBarChart";
import OperationalLineChart from "../../components/charts/OperationalLineChart";
import NPLineChart from "../../components/charts/NPLineChart";
import { useQuery } from "react-query";
import {
  getBorrowerGrowthRate,
  getBorrowerStatistics,
  getDisbursementStatistics,
  getLoanDefaultRate,
  getLoanProductSummary,
  getLoanTenure,
} from "../../sdk/reports/reports";
import { useState } from "react";
import { useFormatAmount } from "../../hooks/useFormatAmount";
import { SkeletonDashboard } from "../../skeletons/DashboardSkeleton";

const Analytics = () => {
  const [borrowerStatistics, setBorrowerStatistics] = useState([]);
  const [borrowerGrowthStatistics, setBorrowerGrowthStatistics] = useState([]);
  const [disbursementStatistics, setDisbursementStatistics] = useState([]);
  const [tenureStatistics, setTenureStatistics] = useState([]);
  const [loanProductsStatistics, setLoanProductsStatistics] = useState([]);
  const [loanDefaultStatistics, setLoanDefaultStatistics] = useState([]);
  const [metaInfo, setMetaInfo] = useState({});
  const [meta, setMeta] = useState({});
  const [disburseMeta, setDisburseMeta] = useState({});
  const formatAmount = useFormatAmount();

  const { isFetching } = useQuery({
    queryKey: ["get borrower statistics"],
    queryFn: async () => {
      const response = await getBorrowerStatistics();

      return response?.data?.data;
    },
    onSuccess: (data) => {
      setBorrowerStatistics(data?.monthlyBorrowerCounts);
      setMetaInfo(data);
    },
  });

  useQuery({
    queryKey: ["get borrower growth rate"],
    queryFn: async () => {
      const response = await getBorrowerGrowthRate();

      return response?.data?.data;
    },
    onSuccess: (data) => {
      setBorrowerGrowthStatistics(data?.monthlyBorrowerGrowthRate);
      setMeta(data);
    },
  });

  useQuery({
    queryKey: ["get disbursement stats"],
    queryFn: async () => {
      const response = await getDisbursementStatistics();

      return response?.data?.data;
    },
    onSuccess: (data) => {
      setDisbursementStatistics(data?.monthlyDisbursements);
      setDisburseMeta(data);
    },
  });

  useQuery({
    queryKey: ["get loan tenure"],
    queryFn: async () => {
      const response = await getLoanTenure();
      return response?.data?.data;
    },
    onSuccess: (data) => {
      setTenureStatistics(data);
    },
  });

  useQuery({
    queryKey: ["get loan product summary"],
    queryFn: async () => {
      const response = await getLoanProductSummary();
      return response?.data?.data;
    },
    onSuccess: (data) => {
      setLoanProductsStatistics(data);
    },
  });

  useQuery({
    queryKey: ["get loan default rate"],
    queryFn: async () => {
      const response = await getLoanDefaultRate();
      return response?.data?.data;
    },
    onSuccess: (data) => {
      setLoanDefaultStatistics(data);
    },
  });

  return (
    <>
      {isFetching ? (
        <SkeletonDashboard />
      ) : (
        <div className="w-full min-h-screen overflow-y-auto">
          <div className="px-4 sm:px-4 md:px-6 lg:px-4 mb-[20px]">
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center text-[#074073] text-lg sm:text-xl md:text-2xl font-bold">
                Analytics
              </div>
            </div>
            <div>
              <span className="text-xs sm:text-sm md:text-base font-medium text-black">
                Total Borrowers & Growth Rate
              </span>
              <div
                className=""
                style={{ borderBottom: "0.7px solid #000000", width: "100%" }}
              ></div>
            </div>

            <div className="flex flex-col lg:flex-row justify-between mt-[20px] gap-3">
              {/* Left Card */}
              <div className="lg:w-[70%] w-full bg-[#FFFFFF] rounded-[20px] px-3">
                <div className="mt-5 text-[16px] text-[#074073] font-[700] px-6">
                  Total Borrowers
                </div>
                <div className="flex flex-col sm:flex-row mt-5">
                  <div className="px-6 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Total Borrowers
                    </div>
                    <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                      {metaInfo?.totalBorrowers ?? 0}
                    </div>
                  </div>
                  <div className="px-6 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Active
                    </div>
                    <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                      {metaInfo?.totalBorrowersWithLoans ?? 0}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <SingleLineChart borrowerStatistics={borrowerStatistics} />
                </div>
              </div>

              {/* Right Cards */}
              <div className="lg:w-[29%] w-full flex flex-col gap-3">
                <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col">
                  <p className="text-[#000000] text-[15px] font-bold py-2 sm:py-5">
                    New borrowers this month
                  </p>
                  <p className="text-[#000000] text-[24px] font-normal py-2 sm:py-5">
                    {metaInfo?.netBorrowersThisMonth ?? 0}
                  </p>
                </div>
                <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col">
                  <p className="text-[#000000] text-[15px] font-bold py-2 sm:py-5">
                    New borrowers last year
                  </p>
                  <p className="text-[#000000] text-[24px] font-normal py-2 sm:py-5">
                    {metaInfo?.netBorrowersLastYearSameMonth ?? 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row justify-between mt-5 gap-3">
              {/* Left Card */}
              <div className="lg:w-[70%] w-full bg-[#FFFFFF] rounded-[20px] px-3">
                <div className="mt-5 text-[16px] text-[#074073] font-[700] px-6">
                  Borrowers Growth Rate
                </div>
                <div className="flex flex-col sm:flex-row mt-5">
                  <div className="px-6 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      This month
                    </div>
                    <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                      {meta?.growthRateThisMonth ?? 0.0}
                    </div>
                  </div>
                  <div className="px-6 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      This year
                    </div>
                    <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                      {meta?.netGrowthRateThisYear ?? 0}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <LineAxisChart
                    borrowerGrowthStatistics={borrowerGrowthStatistics}
                  />
                </div>
              </div>

              {/* Right Cards */}
              <div className="lg:w-[29%] w-full flex flex-col gap-3">
                <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col">
                  <p className="text-[#000000] text-[15px] font-bold py-2 sm:py-5">
                    Borrowers growth rate this year (%)
                  </p>
                  <p className="text-[#06B217] text-[24px] font-normal py-2 sm:py-5">
                    {meta?.netGrowthRateThisYear ?? 0.0}
                  </p>
                </div>
                <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col">
                  <p className="text-[#000000] text-[15px] font-bold py-2 sm:py-5">
                    Borrowers growth rate last year (%)
                  </p>
                  <p className="text-[#FF5757] text-[24px] font-normal py-2 sm:py-5">
                    {meta?.netGrowthRateLastYear ?? 0.0}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <span className="text-xs sm:text-sm md:text-base font-medium text-black">
                Loan Performance
              </span>
              <div
                className=""
                style={{ borderBottom: "0.7px solid #000000", width: "100%" }}
              ></div>
            </div>

            <div className="w-full h-[450px] flex flex-col lg:flex-row justify-between mt-5">
              {/* Left Card */}
              <div className="lg:w-[58%] h-full bg-[#FFFFFF] rounded-[20px] px-3">
                <div className="mt-3 flex items-center justify-between px-3">
                  <div className=" text-[16px] text-[#074073] font-[700]">
                    Loan Summary
                  </div>
                  <select
                    className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                    defaultValue=""
                  >
                    <option value="">-- Select Loan Type</option>
                    <option value="flash_loan">Flash Loan</option>
                    <option value="normal_loan">Normal Loan</option>
                    <option value="emergency_loan">Emergency Loan</option>
                    <option value="check_off">Check-off Loan</option>
                    <option value="development_loan">Development Loan</option>
                  </select>
                </div>
                <div className="flex flex-col sm:flex-row mt-3">
                  <div className="px-5 mt-2 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Period
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      Apr 1, 2025 - Apr 30, 2025
                    </div>
                  </div>
                </div>
                <div className="h-[77%] w-full">
                  <StackedBarChart
                    loanProductsStatistics={loanProductsStatistics}
                  />
                </div>
              </div>

              {/* Right Cards */}
              <div className="lg:w-[40%] h-[450px] flex flex-col rounded-[20px] justify-between">
                <div className="w-full flex justify-between">
                  <div className="flex flex-col bg-[#FFFFFF] h-[183px] rounded-[20px] w-[260px] px-3">
                    <p className="text-[#000000] text-[15px] font-bold py-2 sm:py-5">
                      Principal Due
                    </p>
                    <p className="text-[#000000] text-[24px] font-normal py-2 sm:py-5">
                      KES 130,000.44
                    </p>
                  </div>
                  <div className="flex flex-col bg-[#FFFFFF] h-[183px] rounded-[20px] w-[260px] px-3">
                    <p className="text-[#000000] text-[15px] font-bold py-2 sm:py-5">
                      Fees Due
                    </p>
                    <p className="text-[#000000] text-[24px] font-normal py-2 sm:py-5">
                      KES 32,983.86
                    </p>
                  </div>
                </div>
                <div className="flex w-full justify-between">
                  <div className="flex flex-col bg-[#FFFFFF] h-[183px] rounded-[20px] w-[260px] px-3">
                    <p className="text-[#000000] text-[15px] font-bold py-2 sm:py-5">
                      Interest Due
                    </p>
                    <p className="text-[#000000] text-[24px] font-normal py-2 sm:py-5">
                      KES 78,999.67
                    </p>
                  </div>
                  <div className="flex flex-col bg-[#FFFFFF] h-[183px] rounded-[20px] w-[260px] px-3">
                    <p className="text-[#000000] text-[15px] font-bold py-2 sm:py-5">
                      Total Due
                    </p>
                    <p className="text-[#000000] text-[24px] font-normal py-2 sm:py-5">
                      KES 278,911.88
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row justify-between mt-10 gap-3">
              {/* Left Card */}
              <div className="lg:w-[70%] w-full bg-[#FFFFFF] rounded-[20px] px-3">
                <div className="mt-2 flex items-center justify-between px-3">
                  <div className="text-[15px] text-[#074073] font-[700]">
                    Total Loans Disbursed
                  </div>
                  <select
                    className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                    defaultValue=""
                  >
                    <option value="">-- Select Loan Type</option>
                    <option value="flash_loan">Flash Loan</option>
                    <option value="normal_loan">Normal Loan</option>
                    <option value="emergency_loan">Emergency Loan</option>
                    <option value="check_off">Check-off Loan</option>
                    <option value="development_loan">Development Loan</option>
                  </select>
                </div>

                <div className="flex flex-col sm:flex-row mt-3">
                  <div className="px-6 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      This month
                    </div>
                    <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                      {disburseMeta?.countLoansThisMonth ?? 0}
                    </div>
                  </div>
                  <div className="px-6 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Total this year
                    </div>
                    <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                      {disburseMeta?.countLoansLastYear ?? 0}
                    </div>
                  </div>

                  <div className="px-6 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Total amount this year
                    </div>
                    <div className="mt-1 text-[16px] text-[#000000] font-[400]">
                      {disburseMeta?.totalDisbursedThisMonth ?? 0.0}
                    </div>
                  </div>
                </div>
                <div className="mt-2 px-3">
                  <SecondBarChart
                    disbursementStatistics={disbursementStatistics}
                  />
                </div>
              </div>

              {/* Right Cards */}
              <div className="lg:w-[29%] w-full flex flex-col gap-3">
                <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col">
                  <p className="text-[#000000] text-[15px] font-bold py-2 sm:py-5">
                    Total disbursed this month
                  </p>
                  <p className="text-[#000000] text-[24px] font-normal py-2 sm:py-5">
                    {formatAmount(disburseMeta?.totalDisbursedThisMonth) ?? 0.0}
                  </p>
                </div>
                <div className="bg-[#FFFFFF] h-[183px] rounded-[20px] px-4 flex flex-col">
                  <p className="text-[#000000] text-[15px] font-bold py-2 sm:py-5">
                    Total disbursed last year
                  </p>
                  <p className="text-[#000000] text-[24px] font-normal py-2 sm:py-5">
                    {formatAmount(disburseMeta?.totalDisbursedLastYear) ?? 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row justify-between mt-3 gap-3">
              <div className="lg:w-[50%] w-full bg-[#FFFFFF] rounded-[20px] px-3">
                <div className="flex flex-col sm:flex-row mt-5">
                  <div className="mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      This month(%)
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      0.0%
                    </div>
                  </div>
                  <div className="mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Total this year
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      0.0%
                    </div>
                  </div>
                  <div className="px-5 mt-3 sm:mt-0">
                    <div className="text-[12px] text-[#737373] font-[400]">
                      Last year
                    </div>
                    <div className="mt-1 text-[14px] text-[#000000] font-[400]">
                      0.0%
                    </div>
                  </div>
                </div>
                <div>
                  <HeatmapChart loanDefaultStatistics={loanDefaultStatistics} />
                </div>
              </div>

              <div className="lg:w-[50%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
                <div className="mt-5 text-[16px] text-[#074073] font-[700] px-6">
                  Average Loan Tenure
                </div>
                <div className="h-[90%] w-full">
                  <LoanTenure tenureStatistics={tenureStatistics} />
                </div>
              </div>
            </div>

            <div className="mt-10">
              <span className="text-xs sm:text-sm md:text-base font-medium text-black">
                Financial Health
              </span>
              <div
                className=""
                style={{ borderBottom: "0.7px solid #000000", width: "100%" }}
              ></div>
            </div>

            <div className="flex flex-col lg:flex-row justify-between mt-4 gap-3">
              <div className="lg:w-[100%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
                <div className="mt-5 flex items-center justify-between px-6">
                  <div className="text-[15px] text-[#074073] font-[700]">
                    Total Revenue / Total Expenses / Net Profit
                  </div>
                  <select
                    className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                    defaultValue=""
                  >
                    <option value="">--Select Year</option>
                    <option value="2021">2021</option>
                    <option value="2022">2022</option>
                    <option value="2023">2023</option>
                    <option value="2024">2024</option>
                    <option value="2025">2025</option>
                  </select>
                </div>
                <div className="mt-5 mb-5">
                  <FinancialBarChart />
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row justify-between mt-4 gap-3">
              <div className="lg:w-[100%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
                <div className="mt-5 flex items-center justify-between px-6">
                  <div className="text-[15px] text-[#074073] font-[700]">
                    Operational Efficiency Ratio
                  </div>
                  <select
                    className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                    defaultValue=""
                  >
                    <option value="">--Select Year</option>
                    <option value="2021">2021</option>
                    <option value="2022">2022</option>
                    <option value="2023">2023</option>
                    <option value="2024">2024</option>
                    <option value="2025">2025</option>
                  </select>
                </div>
                <div className="mt-5 mb-5">
                  <OperationalLineChart />
                </div>
              </div>
            </div>

            <div className="mt-10">
              <span className="text-xs sm:text-sm md:text-base font-medium text-black">
                Liquidity & Risk
              </span>
              <div
                className=""
                style={{ borderBottom: "0.7px solid #000000", width: "100%" }}
              ></div>
            </div>

            <div className="flex flex-col lg:flex-row justify-between mt-4 gap-3">
              <div className="lg:w-[100%] w-full flex flex-col bg-[#FFFFFF] rounded-[20px] gap-3">
                <div className="mt-5 flex items-center justify-between px-6">
                  <div className="text-[15px] text-[#074073] font-[700]">
                    Non-Performing Loans (NPL) Ratio (%)
                  </div>
                  <select
                    className="border border-[#D5D5D5] rounded-md px-1.5 text-sm text-[#000000] appearance-none focus:outline-none"
                    defaultValue=""
                  >
                    <option value="">--Select Year</option>
                    <option value="2021">2021</option>
                    <option value="2022">2022</option>
                    <option value="2023">2023</option>
                    <option value="2024">2024</option>
                    <option value="2025">2025</option>
                  </select>
                </div>

                <div className="mt-5 mb-5">
                  <NPLineChart />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Analytics;
